import { createContext, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';

// Create authentication context
export const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  login: () => {},
  logout: () => {},
  register: () => {}
});

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Check for existing auth on mount
  useEffect(() => {
    const checkAuthentication = async () => {
      setIsLoading(true);
      try {
        // Check if token exists in local storage
        const token = sessionStorage.getItem('token');
        
        if (token) {
          // Validate token with API (this is a mock implementation)
          const API_URL = import.meta.env.VITE_API_URL || '/api';
          
          try {
            const response = await fetch(`${API_URL}/auth/me`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            
            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
            } else {
              // Token invalid, remove it
              sessionStorage.removeItem('token');
              setUser(null);
            }
          } catch (error) {
            console.error('Auth check failed:', error);
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthentication();
  }, []);
  
  // Login function
  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }
      
      const data = await response.json();
      
      if (data.token) {
        sessionStorage.setItem('token', data.token);
        if (data.user) {
          sessionStorage.setItem('user', JSON.stringify(data.user));
          setUser(data.user);
        }
        return { success: true };
      } else {
        throw new Error('No token received');
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.message || 'Login failed'
      };
    } finally {
      setIsLoading(false);
    }
  };
  
  // Logout function
  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };
  
  // Register function
  const register = async (userData) => {
    setIsLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }
      
      const data = await response.json();
      
      if (data.token) {
        sessionStorage.setItem('token', data.token);
        if (data.user) {
          sessionStorage.setItem('user', JSON.stringify(data.user));
          setUser(data.user);
        }
        return { success: true };
      } else {
        throw new Error('No token received');
      }
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    } finally {
      setIsLoading(false);
    }
  };
  
  // Calculate isAuthenticated based on user state
  const isAuthenticated = !!user;
  
  return (
    <AuthContext.Provider value={{ 
      user,
      isAuthenticated,
      isLoading,
      login,
      logout,
      register
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using the auth context
export const useAuth = () => useContext(AuthContext);

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
}; 
